import { useState } from 'react';
import { usePEOAssignments } from '@/hooks/use-assignment-management';
import { useProviderQuotes } from '@/components/peo/dashboard/hooks/useProviderQuotes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { 
  Plus, 
  Clock, 
  CheckCircle, 
  Send, 
  Edit3, 
  MessageSquare, 
  Calendar,
  DollarSign,
  FileText,
  Users,
  Building2,
  AlertCircle,
  ArrowRight,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { ApplicationAssignment } from '@/types/assignment.types';
import { PEOQuote } from '@/types/quotes.types';
import { ApplicationContextPanel } from './ApplicationContextPanel';
import { QuoteTemplateSelector } from './QuoteTemplateSelector';

interface QuotePipelineProps {
  providerId: string;
}

interface PipelineStage {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  color: string;
  items: (ApplicationAssignment | PEOQuote)[];
}

export const QuotePipeline = ({ providerId }: QuotePipelineProps) => {
  const { assignments } = usePEOAssignments(providerId);
  const { data: quotes = [] } = useProviderQuotes(providerId);
  
  const [selectedItem, setSelectedItem] = useState<ApplicationAssignment | PEOQuote | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ApplicationAssignment | null>(null);

  // Create pipeline stages
  const stages: PipelineStage[] = [
    {
      id: 'new_assignments',
      title: 'New Assignments',
      icon: Clock,
      color: 'bg-blue-100 border-blue-200',
      items: assignments.filter(a => a.assignment_status === 'pending')
    },
    {
      id: 'draft_quotes',
      title: 'Draft Quotes',
      icon: Edit3,
      color: 'bg-orange-100 border-orange-200',
      items: quotes.filter(q => !q.is_submitted)
    },
    {
      id: 'under_review',
      title: 'Under Review',
      icon: Eye,
      color: 'bg-purple-100 border-purple-200',
      items: quotes.filter(q => q.status === 'pending' && q.is_submitted)
    },
    {
      id: 'submitted',
      title: 'Submitted',
      icon: Send,
      color: 'bg-green-100 border-green-200',
      items: quotes.filter(q => q.is_submitted && q.status === 'pending')
    },
    {
      id: 'closed',
      title: 'Closed',
      icon: CheckCircle,
      color: 'bg-gray-100 border-gray-200',
      items: quotes.filter(q => ['accepted', 'declined', 'expired'].includes(q.status))
    }
  ];

  const handleCreateQuote = (assignment: ApplicationAssignment) => {
    setSelectedAssignment(assignment);
    setIsCreateQuoteOpen(true);
  };

  const handleViewDetails = (item: ApplicationAssignment | PEOQuote) => {
    setSelectedItem(item);
    setIsDetailsOpen(true);
  };

  const isAssignment = (item: ApplicationAssignment | PEOQuote): item is ApplicationAssignment => {
    return 'assignment_status' in item;
  };

  const getItemTitle = (item: ApplicationAssignment | PEOQuote) => {
    if (isAssignment(item)) {
      return item.application?.company_legal_name || 
             `${item.application?.first_name} ${item.application?.last_name}`;
    }
    return item.company_name || item.applicant_name || 'Untitled Quote';
  };

  const getItemSubtitle = (item: ApplicationAssignment | PEOQuote) => {
    if (isAssignment(item)) {
      return `${item.application?.w2_employee_count} employees • ${item.application?.primary_industry}`;
    }
    return `${item.monthly_cost ? `$${item.monthly_cost}/month` : 'No pricing'} • ${format(new Date(item.created_at), 'MMM d')}`;
  };

  const getPriorityColor = (item: ApplicationAssignment | PEOQuote) => {
    if (isAssignment(item)) {
      switch (item.priority) {
        case 'urgent': return 'bg-red-500';
        case 'high': return 'bg-orange-500';
        case 'normal': return 'bg-blue-500';
        case 'low': return 'bg-gray-500';
        default: return 'bg-gray-500';
      }
    }
    return 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">Quote Pipeline</h3>
          <p className="text-muted-foreground">Manage your quote workflow from assignment to submission</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline">
            <MessageSquare className="mr-2 h-4 w-4" />
            Team Chat
          </Button>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Quote
          </Button>
        </div>
      </div>

      {/* Pipeline Stages */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {stages.map((stage) => (
          <div key={stage.id} className="space-y-4">
            <div className={`${stage.color} rounded-lg p-4 border-2`}>
              <div className="flex items-center gap-2 mb-2">
                <stage.icon className="h-5 w-5" />
                <h4 className="font-semibold">{stage.title}</h4>
                <Badge variant="secondary" className="ml-auto">
                  {stage.items.length}
                </Badge>
              </div>
            </div>

            <div className="space-y-3 min-h-[400px]">
              {stage.items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="w-12 h-12 mx-auto mb-2 opacity-50">
                    <stage.icon className="w-full h-full" />
                  </div>
                  <p className="text-sm">No items</p>
                </div>
              ) : (
                stage.items.map((item) => (
                  <Card key={item.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium truncate">{getItemTitle(item)}</h5>
                            <p className="text-sm text-muted-foreground truncate">
                              {getItemSubtitle(item)}
                            </p>
                          </div>
                          {isAssignment(item) && (
                            <div 
                              className={`w-2 h-2 rounded-full ${getPriorityColor(item)}`}
                              title={`${item.priority} priority`}
                            />
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(item.created_at), 'MMM d, h:mm a')}
                          {isAssignment(item) && item.due_date && (
                            <>
                              <ArrowRight className="h-3 w-3" />
                              <Clock className="h-3 w-3" />
                              {format(new Date(item.due_date), 'MMM d')}
                            </>
                          )}
                        </div>

                        <div className="flex gap-2">
                          {isAssignment(item) && stage.id === 'new_assignments' ? (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleViewDetails(item)}
                                className="flex-1"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => handleCreateQuote(item)}
                                className="flex-1"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Quote
                              </Button>
                            </>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleViewDetails(item)}
                              className="w-full"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View Details
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedItem && getItemTitle(selectedItem)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6">
              {isAssignment(selectedItem) ? (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant={selectedItem.priority === 'urgent' ? 'destructive' : 'secondary'}>
                      {selectedItem.priority} Priority
                    </Badge>
                    <Badge variant="outline">
                      Assignment • {selectedItem.assignment_status.replace('_', ' ')}
                    </Badge>
                  </div>
                  
                  {selectedItem.assignment_notes && (
                    <div className="p-4 bg-blue-50 rounded-lg mb-4">
                      <h5 className="font-medium text-blue-900 mb-2">Assignment Notes</h5>
                      <p className="text-blue-800">{selectedItem.assignment_notes}</p>
                    </div>
                  )}
                  
                  <ApplicationContextPanel application={selectedItem.application!} />
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">{selectedItem.status}</Badge>
                    {selectedItem.monthly_cost && (
                      <Badge variant="outline">
                        <DollarSign className="h-3 w-3 mr-1" />
                        ${selectedItem.monthly_cost}/month
                      </Badge>
                    )}
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-medium mb-2">Quote Summary</h5>
                    <p>{selectedItem.quote_summary || 'No summary available'}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Quote Dialog */}
      <Dialog open={isCreateQuoteOpen} onOpenChange={setIsCreateQuoteOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Quote</DialogTitle>
            <DialogDescription>
              Create a quote for {selectedAssignment && getItemTitle(selectedAssignment)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAssignment && (
            <QuoteTemplateSelector 
              assignment={selectedAssignment}
              onQuoteCreated={() => {
                setIsCreateQuoteOpen(false);
                setSelectedAssignment(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};